import { intervalToDuration, isToday, isTomorrow, isYesterday } from 'date-fns';
import { pipe } from 'fp-ts/function';
import { match } from 'ts-pattern';
import i18n from 'i18next';
import { parseDate } from '../../datetime';
import { createDateTimeFormat, createRelativeTimeFormat, createTimeFormatConfig } from './formats';
import { determineDurationField } from './utils';
import { TimeFormat } from './types';

const ensureUnitKey = (
  value: Intl.RelativeTimeFormatUnitSingular | 'justNow',
): Intl.RelativeTimeFormatUnitSingular => (value === 'justNow' ? 'minute' : value);

/**
 * Returns a localized relative date/time offset and an actual date
 */
export const localizeRelativeTimeBetween = (a: Date, b: Date = parseDate(), locale?: string) => {
  const formatter = createRelativeTimeFormat(locale);

  return pipe(
    { start: a, end: b },
    intervalToDuration,
    determineDurationField,
    ([count, key]): [number, Intl.RelativeTimeFormatUnitSingular] => [count, ensureUnitKey(key)],
    ([count, key]) => formatter.format(-count, key),
  );
};

export type LocalizedRelativeDateOptions = {
  locale?: string;
  relative?: boolean;
  showTime?: boolean;
  timeStyle?: TimeFormat;
  timeZone?: string;
};

export const localizeRelativeTimeSince = (
  date: Date,
  options: LocalizedRelativeDateOptions = {},
) => {
  const isRelativeAnd = (f: (date: Date) => boolean) => (d: Date) =>
    Boolean(options.relative) && f(d);

  const timePart = options.showTime
    ? createDateTimeFormat(
        options.locale,
        createTimeFormatConfig(options.timeStyle ?? TimeFormat.Locale, options.timeZone),
      ).format(date)
    : undefined;

  const translate = (key: string) => () =>
    i18n.t(`common:datetime.relative.${key}${timePart ? 'WithTime' : ''}`, { time: timePart });

  return match(date)
    .when(isRelativeAnd(isYesterday), translate('yesterday'))
    .when(isRelativeAnd(isToday), translate('today'))
    .when(isRelativeAnd(isTomorrow), translate('tomorrow'))
    .otherwise((d) => localizeRelativeTimeBetween(d, undefined, options.locale));
};
